import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface MilestoneProgress {
  completedSteps: string[]; // Array of completed step IDs
  activeStep: number; // Current active step index
  totalSteps: number; // Total number of steps
}

interface PrepProgressState {
  budget: MilestoneProgress;
  preApproval: MilestoneProgress;
  propertyAssessment: MilestoneProgress;
  marketNavigation: MilestoneProgress;
  closingProcess: MilestoneProgress;
}

const initialMilestoneState: MilestoneProgress = {
  completedSteps: [],
  activeStep: 0,
  totalSteps: 0,
};

const initialState: PrepProgressState = {
  budget: { ...initialMilestoneState, totalSteps: 5 }, // budgetTips.length
  preApproval: { ...initialMilestoneState, totalSteps: 5 }, // preApprovalTips.length
  propertyAssessment: { ...initialMilestoneState, totalSteps: 4 }, // inspectionCategories.length
  marketNavigation: { ...initialMilestoneState, totalSteps: 2 }, // marketSteps.length (updated after removing contract steps)
  closingProcess: { ...initialMilestoneState, totalSteps: 3 }, // closingSteps.length (timeline, terms, concepts)
};

type MilestoneType = keyof PrepProgressState;

interface MilestoneAction {
  milestone: MilestoneType;
  stepId?: string;
  activeStep?: number;
  completedSteps?: string[];
  totalSteps?: number;
}

const prepProgressSlice = createSlice({
  name: 'prepProgress',
  initialState,
  reducers: {
    setActiveStep: (
      state,
      action: PayloadAction<Pick<MilestoneAction, 'milestone' | 'activeStep'>>
    ) => {
      const { milestone, activeStep } = action.payload;
      if (activeStep !== undefined) {
        state[milestone].activeStep = activeStep;
      }
    },
    completeStep: (
      state,
      action: PayloadAction<Pick<MilestoneAction, 'milestone' | 'stepId'>>
    ) => {
      const { milestone, stepId } = action.payload;
      if (stepId && !state[milestone].completedSteps.includes(stepId)) {
        state[milestone].completedSteps.push(stepId);
      }
    },
    uncompleteStep: (
      state,
      action: PayloadAction<Pick<MilestoneAction, 'milestone' | 'stepId'>>
    ) => {
      const { milestone, stepId } = action.payload;
      if (stepId) {
        state[milestone].completedSteps = state[
          milestone
        ].completedSteps.filter((id) => id !== stepId);
      }
    },
    resetMilestoneProgress: (
      state,
      action: PayloadAction<{ milestone: MilestoneType }>
    ) => {
      const { milestone } = action.payload;
      state[milestone].completedSteps = [];
      state[milestone].activeStep = 0;
    },
    setCompletedSteps: (
      state,
      action: PayloadAction<
        Pick<MilestoneAction, 'milestone' | 'completedSteps'>
      >
    ) => {
      const { milestone, completedSteps } = action.payload;
      if (completedSteps) {
        state[milestone].completedSteps = completedSteps;
      }
    },
    setTotalSteps: (
      state,
      action: PayloadAction<Pick<MilestoneAction, 'milestone' | 'totalSteps'>>
    ) => {
      const { milestone, totalSteps } = action.payload;
      if (totalSteps !== undefined) {
        state[milestone].totalSteps = totalSteps;
      }
    },
  },
});

export const {
  setActiveStep,
  completeStep,
  uncompleteStep,
  resetMilestoneProgress,
  setCompletedSteps,
  setTotalSteps,
} = prepProgressSlice.actions;

export default prepProgressSlice.reducer;
